import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Loader2, MessageCircle, PauseCircle, CheckCircle2,
  XCircle, Clock, Download, Copy, Mail, Search, Plus, UsersRound, ExternalLink, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending: { icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending' },
  active: { icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Active' },
  declined: { icon: XCircle, className: 'bg-stone-100 text-stone-600 border-stone-200', label: 'Declined' },
  revoked: { icon: XCircle, className: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Revoked' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

/**
 * Super-admin drill-down: every parent linked to therapists at this practice.
 * Powers your outreach — copy email, mailto, or export CSV to bulk-message.
 */
export default function AdminPracticeParents() {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteSubmit, setInviteSubmit] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRaw, setBulkRaw] = useState('');
  const [bulkSubmit, setBulkSubmit] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => { fetchParents(); /* eslint-disable-next-line */ }, [practiceId]);

  const fetchParents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/practices/${practiceId}/parents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Super-admin access required');
        navigate('/');
      } else if (err?.response?.status === 404) {
        toast.error('Practice not found');
        navigate('/admin/practices');
      } else {
        toast.error('Failed to load parents');
      }
    } finally {
      setLoading(false);
    }
  };

  const rows = (data?.parents || []).filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (p.parent_email || '').toLowerCase().includes(s) ||
      (p.parent_name || '').toLowerCase().includes(s) ||
      (p.therapist_name || '').toLowerCase().includes(s)
    );
  });

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); toast.success(`Copied ${email}`); }
    catch { toast.error('Could not copy'); }  };

  const submitInvite = async () => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) return toast.error('Enter a parent email');
    setInviteSubmit(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/practices/${practiceId}/invite-parent`,
        { email, name: inviteForm.name.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.email_sent) {
        toast.success(`Invite email sent to ${email}`);
      } else {
        toast.warning('Parent created but email delivery not confirmed. Copy the invite URL from the response if needed.');
      }
      setInviteOpen(false);
      setInviteForm({ email: '', name: '' });
      fetchParents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not invite');
    } finally {
      setInviteSubmit(false);
    }
  };

  // Bulk invite — paste one email per line (or comma separated). Backend
  // fills any free seats immediately, queues the rest, and emails the
  // school admin a Paystack top-up link automatically.
  const submitBulkInvite = async () => {
    const raw = bulkRaw.trim();
    if (!raw) return toast.error('Paste at least one email');
    setBulkSubmit(true);
    setBulkResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/practices/${practiceId}/bulk-invite-parents`,
        { raw },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkResult(data);
      const bits = [];
      if (data.invited_count) bits.push(`${data.invited_count} invited`);
      if (data.queued_count) bits.push(`${data.queued_count} queued`);
      if (data.skipped_count) bits.push(`${data.skipped_count} skipped`);
      toast.success(bits.join(' · ') || 'Processed');
      if (data.extra_seats_needed > 0 && data.admin_email_sent) {
        toast.success(`Payment link emailed to school admin for ${data.extra_seats_needed} extra seat${data.extra_seats_needed === 1 ? '' : 's'}`);
      } else if (data.extra_seats_needed > 0 && !data.checkout_url) {
        toast.warning('Extra seats needed but Paystack checkout could not be generated. Check practice pricing.');
      }
      fetchParents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not process bulk invite');
    } finally {
      setBulkSubmit(false);
    }
  };

  const removePendingInvite = async (email) => {
    if (!window.confirm(`Remove ${email} from the queue?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/practices/${practiceId}/remove-pending-invite`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Removed');
      fetchParents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove');
    }
  };

  // If Paystack succeeded but the webhook never landed (URL misconfigured,
  // metadata stripped, delivered to preview, etc), the super admin can
  // paste the reference and we'll verify with Paystack + flush the queue.
  const [reconciling, setReconciling] = useState(false);
  const reconcilePayment = async () => {
    const ref = window.prompt(
      'Paste the Paystack reference from the payment confirmation email\n(starts with "pfp_"). Leave blank to use the reference we stored when generating the last payment link.',
      '',
    );
    if (ref === null) return; // cancelled
    setReconciling(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/practices/${practiceId}/reconcile-payment`,
        { reference: ref.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.ok) {
        toast.success(data.message || 'Reconciled');
      } else {
        toast.warning(data.message || 'Could not reconcile');
      }
      fetchParents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reconcile');
    } finally {
      setReconciling(false);
    }
  };

  const copyAllEmails = async () => {
    const list = rows.filter((r) => r.parent_email).map((r) => r.parent_email).join(', ');
    if (!list) return toast.error('No emails to copy');
    await navigator.clipboard.writeText(list);
    toast.success(`Copied ${rows.length} email${rows.length === 1 ? '' : 's'}`);
  };

  const downloadCsv = () => {
    if (!rows.length) return toast.error('No rows to export');
    const headers = [
      'parent_email', 'parent_name', 'therapist_name', 'child_name', 'status',
      'initiated_by', 'whatsapp_verified', 'whatsapp_paused', 'whatsapp_phone',
      'sponsored_by_this_practice', 'joined_at', 'link_created_at', 'last_pushed_at',
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      lines.push([
        r.parent_email, r.parent_name, r.therapist_name, r.child_name, r.status,
        r.initiated_by, r.parent_whatsapp_verified, r.parent_whatsapp_paused,
        r.parent_whatsapp_phone, r.parent_sponsored_by_us,
        r.parent_joined_at, r.created_at, r.last_pushed_at,
      ].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(data?.practice_name || 'practice').replace(/\W+/g, '_')}_parents.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/practices')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-xl font-serif text-primary-900">
                Parents · {data?.practice_name || '…'}
              </h1>
              <p className="text-xs text-stone-500">
                {data ? `${data.count} link${data.count === 1 ? '' : 's'} · ${data.seats_used}/${data.seats_purchased} seats used` : ''}
              </p>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* Filters + actions */}
          <div className="flex flex-col md:flex-row gap-3 mb-5">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search parent email, name, or therapist"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-11 pl-9 rounded-xl"
                data-testid="parents-filter"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-11 px-3 rounded-xl border border-stone-200 bg-white"
              data-testid="status-filter"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="declined">Declined</option>
              <option value="revoked">Revoked</option>
            </select>
            <div className="flex gap-2">
              <Button
                onClick={() => setBulkOpen(true)}
                className="rounded-full bg-primary-600 hover:bg-primary-700 text-white"
                data-testid="admin-bulk-invite-btn"
              >
                <UsersRound className="w-4 h-4 mr-2" /> Bulk invite
              </Button>
              <Button
                onClick={() => setInviteOpen(true)}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="admin-invite-parent-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Invite parent
              </Button>
              <Button variant="outline" onClick={copyAllEmails} className="rounded-full" data-testid="copy-all-emails">
                <Copy className="w-4 h-4 mr-2" /> Copy emails
              </Button>
              <Button variant="outline" onClick={downloadCsv} className="rounded-full" data-testid="download-csv">
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
            </div>
          </div>

          {/* Pending queue — parents added by super-admin waiting for the
              school to top up their seat count. Auto-flushes when Paystack
              webhook activates. */}
          {(data?.pending_invites?.length || 0) > 0 && (
            <Card className="p-4 md:p-5 rounded-2xl border border-amber-200 bg-amber-50/50 mb-5" data-testid="pending-invites-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-medium text-amber-900">
                    {data.pending_invites.length} parent{data.pending_invites.length === 1 ? '' : 's'} waiting for a seat
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Auto-invited the moment the school admin tops up. You don't need to re-add.
                  </p>
                </div>
                <Clock className="w-5 h-5 text-amber-700" />
              </div>
              <ul className="space-y-1 mb-3">
                {data.pending_invites.map((q) => (
                  <li key={q.email} className="flex items-center justify-between text-sm py-1">
                    <span className="text-amber-900" data-testid={`pending-email-${q.email}`}>{q.email}</span>
                    <button
                      type="button"
                      onClick={() => removePendingInvite(q.email)}
                      className="text-amber-700 hover:text-amber-900"
                      data-testid={`remove-pending-${q.email}`}
                      title="Remove from queue"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t border-amber-200 pt-3">
                <p className="text-xs text-amber-800 mb-2">
                  <strong>Already paid?</strong> If the school admin confirmed payment but the queue is still here,
                  the Paystack webhook may not have landed. Verify manually:
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-amber-300 text-amber-900 hover:bg-amber-100"
                  onClick={reconcilePayment}
                  disabled={reconciling}
                  data-testid="reconcile-payment-btn"
                >
                  {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-2" />}
                  Verify payment &amp; flush queue
                </Button>
              </div>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : rows.length === 0 ? (
            <Card className="p-12 text-center rounded-3xl border border-stone-100">
              <Users className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p className="text-stone-500">No parents match your filter.</p>
              {(data?.count || 0) === 0 && (
                <p className="text-xs text-stone-400 mt-2">
                  Therapists at this practice haven't invited any parents yet.
                </p>
              )}
            </Card>
          ) : (
            <div className="space-y-3">
              {rows.map((p) => {
                const badge = STATUS_BADGES[p.status] || STATUS_BADGES.pending;
                const Icon = badge.icon;
                return (
                  <Card
                    key={p.link_id}
                    className="p-4 md:p-5 rounded-2xl border border-stone-100 bg-white"
                    data-testid={`parent-row-${p.link_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <p className="font-medium text-primary-900 truncate" data-testid={`parent-email-${p.link_id}`}>
                            {p.parent_email || p.parent_name || 'Unknown'}
                          </p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${badge.className}`}>
                            <Icon className="w-3 h-3" /> {badge.label}
                          </span>
                          {p.initiated_by === 'parent' && p.status === 'pending' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              Parent-initiated
                            </span>
                          )}
                          {p.parent_sponsored_by_us && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Sponsored
                            </span>
                          )}
                          {p.parent_whatsapp_verified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700">
                              <MessageCircle className="w-3 h-3" /> WhatsApp on
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-500">
                              <MessageCircle className="w-3 h-3" /> No WhatsApp
                            </span>
                          )}
                          {p.parent_whatsapp_paused && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700">
                              <PauseCircle className="w-3 h-3" /> Paused
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                          <span>Therapist: {p.therapist_name || '—'}</span>
                          {p.child_name && <span>Child: {p.child_name}</span>}
                          <span>Joined: {fmtDate(p.parent_joined_at)}</span>
                          <span>Linked: {fmtDate(p.created_at)}</span>
                          {p.last_pushed_at && <span>Last activity pushed: {fmtDate(p.last_pushed_at)}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => copyEmail(p.parent_email)}
                          disabled={!p.parent_email}
                          data-testid={`copy-email-${p.link_id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          asChild
                          disabled={!p.parent_email}
                          data-testid={`mail-parent-${p.link_id}`}
                        >
                          <a href={`mailto:${p.parent_email || ''}?subject=A quick hello from PlayFirst`}>
                            <Mail className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>

      {/* Feb 2026 — Super-admin invite parent on behalf of school */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="admin-invite-parent-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              Invite parent · {data?.practice_name}
            </DialogTitle>
            <DialogDescription>
              You're inviting on behalf of the school. The parent gets the standard sponsored
              invite email — one seat is drawn from the school's cap.
              {data && ` Currently using ${data.seats_used}/${data.seats_purchased}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Parent email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="h-11 rounded-xl"
                placeholder="parent@example.com"
                data-testid="admin-invite-parent-email"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Parent name <span className="text-stone-400">(optional)</span></Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="h-11 rounded-xl"
                placeholder="e.g. Sarah Naidoo"
                data-testid="admin-invite-parent-name"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitInvite}
              disabled={inviteSubmit || !inviteForm.email.trim()}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-invite-parent-submit"
            >
              {inviteSubmit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Bulk invite. Paste a list; backend fills free seats
          immediately and emails the school admin a Paystack top-up link
          for the shortfall. Queue auto-flushes on webhook. */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkResult(null); setBulkRaw(''); } }}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="admin-bulk-invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              Bulk invite parents · {data?.practice_name}
            </DialogTitle>
            <DialogDescription>
              Paste emails (one per line, comma or space separated). We'll invite as many as free seats
              allow, queue the rest, and email the school admin a payment link for the extra seats needed.
              {data && ` Seats: ${data.seats_used}/${data.seats_purchased} used.`}
            </DialogDescription>
          </DialogHeader>
          {!bulkResult ? (
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Parent emails</Label>
                <Textarea
                  value={bulkRaw}
                  onChange={(e) => setBulkRaw(e.target.value)}
                  className="min-h-[180px] rounded-xl font-mono text-sm"
                  placeholder={`sarah@example.com\nkiara@example.com\nnaledi@example.com`}
                  data-testid="admin-bulk-invite-textarea"
                  autoFocus
                />
                <p className="text-xs text-stone-500">
                  Duplicates and invalid entries are auto-skipped.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 mt-2 text-sm" data-testid="admin-bulk-invite-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-2xl font-serif text-emerald-800">{bulkResult.invited_count}</p>
                  <p className="text-xs text-emerald-700 mt-1">Invited</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-2xl font-serif text-amber-800">{bulkResult.queued_count}</p>
                  <p className="text-xs text-amber-700 mt-1">Queued</p>
                </div>
                <div className="rounded-xl bg-stone-100 border border-stone-200 p-3">
                  <p className="text-2xl font-serif text-stone-700">{bulkResult.skipped_count}</p>
                  <p className="text-xs text-stone-600 mt-1">Skipped</p>
                </div>
              </div>
              {bulkResult.extra_seats_needed > 0 && (
                <Card className="p-3 rounded-xl bg-blue-50 border-blue-200">
                  <p className="font-medium text-blue-900 mb-1">
                    {bulkResult.extra_seats_needed} extra seat{bulkResult.extra_seats_needed === 1 ? '' : 's'} needed
                  </p>
                  <p className="text-xs text-blue-800 mb-2">
                    {bulkResult.admin_email_sent
                      ? 'Payment link emailed to the school admin.'
                      : 'Email delivery not confirmed — share the link manually below.'}
                  </p>
                  {bulkResult.checkout_url && (
                    <div className="flex flex-col gap-2">
                      <a
                        href={bulkResult.checkout_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-700 underline break-all"
                        data-testid="bulk-invite-checkout-link"
                      >
                        {bulkResult.checkout_url}
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full self-start"
                        onClick={async () => {
                          await navigator.clipboard.writeText(bulkResult.checkout_url);
                          toast.success('Payment link copied');
                        }}
                        data-testid="copy-checkout-link"
                      >
                        <Copy className="w-3.5 h-3.5 mr-2" /> Copy payment link
                      </Button>
                    </div>
                  )}
                </Card>
              )}
              {bulkResult.skipped_count > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-stone-600">
                    {bulkResult.skipped_count} skipped — click to expand
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {(bulkResult.skipped || []).map((s, i) => (
                      <li key={i} className="text-stone-500">
                        <span className="font-mono">{s.email}</span> — {s.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setBulkOpen(false)}>
              {bulkResult ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult && (
              <Button
                onClick={submitBulkInvite}
                disabled={bulkSubmit || !bulkRaw.trim()}
                className="rounded-full bg-primary-600 hover:bg-primary-700"
                data-testid="admin-bulk-invite-submit"
              >
                {bulkSubmit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Send + auto-order seats
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
